import { baseApi } from './baseApi';
import {
  DailyMenu,
  DailyMenuTemplate,
  MealType,
  CreateDailyMenuRequest,
  UpdateDailyMenuRequest,
  CopyDailyMenuRequest,
  ToggleOptionAvailabilityRequest,
  CreateDailyMenuTemplateRequest,
  UpdateDailyMenuTemplateRequest,
  DailyMenuCountsReport,
  DailyMenuRangeCountsReport,
  DailyMenuDishSuggestion,
} from '../types/dailyMenu';

export const dailyMenuApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ================ DAILY MENU CRUD ================

    getDailyMenusByDate: builder.query<DailyMenu[], { date: string; mealType?: MealType }>({
      query: ({ date, mealType }) => {
        let url = `/daily-menus?date=${date}`;
        if (mealType) {
          url += `&mealType=${mealType}`;
        }
        return url;
      },
      providesTags: ['DailyMenu'],
    }),

    getDailyMenuById: builder.query<DailyMenu, string>({
      query: (id) => `/daily-menus/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'DailyMenu', id }],
    }),

    getLatestDailyMenu: builder.query<DailyMenu, void>({
      query: () => '/daily-menus/latest',
      providesTags: ['DailyMenu'],
    }),

    getActiveDailyMenus: builder.query<DailyMenu[], { mealType?: MealType } | void>({
      query: (params) => {
        let url = '/daily-menus/active';
        if (params && params.mealType) {
          url += `?mealType=${params.mealType}`;
        }
        return url;
      },
      providesTags: ['DailyMenu'],
    }),

    createDailyMenu: builder.mutation<DailyMenu, CreateDailyMenuRequest>({
      query: (body) => ({
        url: '/daily-menus',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['DailyMenu'],
    }),

    updateDailyMenu: builder.mutation<DailyMenu, { id: string } & UpdateDailyMenuRequest>({
      query: ({ id, ...body }) => ({
        url: `/daily-menus/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'DailyMenu', id },
        'DailyMenu',
      ],
    }),

    deleteDailyMenu: builder.mutation<void, string>({
      query: (id) => ({
        url: `/daily-menus/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['DailyMenu'],
    }),

    copyDailyMenuToDate: builder.mutation<DailyMenu, { id: string } & CopyDailyMenuRequest>({
      query: ({ id, ...body }) => ({
        url: `/daily-menus/${id}/copy-to-date`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['DailyMenu'],
    }),

    toggleOptionAvailability: builder.mutation<void, { optionId: string } & ToggleOptionAvailabilityRequest>({
      query: ({ optionId, ...body }) => ({
        url: `/daily-menus/options/${optionId}/availability`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['DailyMenu'],
    }),

    getDailyMenuCounts: builder.query<DailyMenuCountsReport, string>({
      query: (id) => `/daily-menus/${id}/counts`,
      providesTags: (_result, _error, id) => [
        { type: 'DailyMenuCounts', id },
        'DailyMenuCounts',
      ],
    }),

    getDailyMenuCountsRange: builder.query<
      DailyMenuRangeCountsReport,
      { startDate: string; endDate: string; mealType?: MealType }
    >({
      query: ({ startDate, endDate, mealType }) => {
        let url = `/daily-menus/counts/range?startDate=${startDate}&endDate=${endDate}`;
        if (mealType) url += `&mealType=${mealType}`;
        return url;
      },
      providesTags: ['DailyMenuCounts'],
    }),

    /**
     * Recetario de platillos ya usados, para sugerirlos al capturar el menú.
     * Se refresca cuando cambia un menú porque un platillo nuevo debe quedar
     * disponible para el día siguiente.
     */
    getDishSuggestions: builder.query<DailyMenuDishSuggestion[], void>({
      query: () => '/daily-menus/dish-suggestions',
      providesTags: ['DailyMenu'],
    }),

    // ================ TEMPLATES ================

    getTemplates: builder.query<DailyMenuTemplate[], void>({
      query: () => '/daily-menus/templates',
      providesTags: ['DailyMenuTemplate'],
    }),

    getTemplateById: builder.query<DailyMenuTemplate, string>({
      query: (id) => `/daily-menus/templates/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'DailyMenuTemplate', id }],
    }),

    createTemplate: builder.mutation<DailyMenuTemplate, CreateDailyMenuTemplateRequest>({
      query: (body) => ({
        url: '/daily-menus/templates',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['DailyMenuTemplate'],
    }),

    updateTemplate: builder.mutation<DailyMenuTemplate, { id: string } & UpdateDailyMenuTemplateRequest>({
      query: ({ id, ...body }) => ({
        url: `/daily-menus/templates/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'DailyMenuTemplate', id },
        'DailyMenuTemplate',
      ],
    }),

    deleteTemplate: builder.mutation<void, string>({
      query: (id) => ({
        url: `/daily-menus/templates/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['DailyMenuTemplate'],
    }),

    // ================ ANCHOR PRODUCT ================

    getAnchorProductId: builder.query<{ productId: string }, void>({
      query: () => '/daily-menus/anchor-product-id',
    }),
  }),
});

export const {
  // Daily Menu hooks
  useGetDailyMenusByDateQuery,
  useGetDailyMenuByIdQuery,
  useGetLatestDailyMenuQuery,
  useGetActiveDailyMenusQuery,
  useCreateDailyMenuMutation,
  useUpdateDailyMenuMutation,
  useDeleteDailyMenuMutation,
  useCopyDailyMenuToDateMutation,
  useToggleOptionAvailabilityMutation,
  useGetDailyMenuCountsQuery,
  useLazyGetDailyMenuCountsQuery,
  useGetDailyMenuCountsRangeQuery,
  useLazyGetDailyMenuCountsRangeQuery,
  useGetDishSuggestionsQuery,

  // Template hooks
  useGetTemplatesQuery,
  useGetTemplateByIdQuery,
  useCreateTemplateMutation,
  useUpdateTemplateMutation,
  useDeleteTemplateMutation,

  // Anchor product
  useGetAnchorProductIdQuery,
} = dailyMenuApi;
